<template>
  <div
    class="flex w-full flex-col rounded-md bg-white shadow-md"
    @mouseenter.stop="tileIsHovered = true"
    @mouseleave="tileIsHovered = false"
  >
    <div class="flex items-center justify-between border-b px-6 py-4">
      <div class="flex items-center space-x-2">
        <input
          v-if="select"
          type="checkbox"
          :checked="selectedIds.includes(scorecardGroup.id)"
          class="cursor-pointer"
          @input="handleCheckboxInput"
        />
        <lf-h3
          v-tooltip="scorecardGroup.name"
          class="-mb-0 max-w-70 overflow-x-hidden text-ellipsis whitespace-nowrap"
          :class="{
            'cursor-pointer': !pageIsView,
          }"
          @click="canUpdateScoreCardGroup && !pageIsView && openPreviewModal()"
        >
          {{ scorecardGroup.name }}
        </lf-h3>
        <scorecard-id-badge :model-value="scorecardGroup.id" />
        <lf-badge
          v-if="
            scorecardGroup.type === SCORECARD_TYPE_MANUAL ||
            scorecardGroup.status === SCORECARD_STATUS_DRAFT
          "
        >
          <template v-if="scorecardGroup.type === SCORECARD_TYPE_MANUAL">
            {{ $t("SCORECARDS.MANUAL") }}
          </template>
          <template
            v-else-if="scorecardGroup.status === SCORECARD_STATUS_DRAFT"
          >
            {{ $t("SCORECARDS.DRAFT") }}
          </template>
        </lf-badge>
      </div>
      <div class="grid grid-flow-col items-center gap-x-4">
        <template v-if="isWorkflowBuilder || isWorkflowLive">
          <scorecard-favorite-button :model="scorecardGroup" />
        </template>
        <template
          v-if="
            !isWorkflowBuilder &&
            !isWorkflowLive &&
            pageIsView &&
            canUseScorecardActions
          "
        >
          <scorecard-action-menu
            :teleport="false"
            :model="scorecardGroup"
            is-group
            :offset="{ left: -147, top: 5 }"
          />
        </template>
        <template v-if="isWorkflowLive && canRunScoreCardGroup">
          <run-scorecard-button :model="scorecardGroup" is-group />
        </template>
      </div>
    </div>
    <div class="flex flex-col space-y-6 p-6">
      <div class="flex justify-between">
        <p class="line-clamp-2 text-gray-400">
          {{ scorecardGroup.description }}
        </p>
        <div
          v-if="!pageIsView"
          class="flex w-80 items-center"
          :class="results.length ? 'justify-between' : 'justify-end'"
        >
          <span class="font-semibold whitespace-nowrap text-gray-600">
            {{ $t("SCORECARDS.PTS", { points }) }}
          </span>
          <scorecard-risk-tier-pill
            v-if="results.length && riskTier"
            font-size="text-xxs"
            :risk-tier="riskTier"
            :readonly="true"
          />
          <lf-pill
            v-if="results.length && groupStatus"
            :color-index="getColorId(groupStatus?.color)"
          >
            {{ groupStatus?.name || "-" }}
          </lf-pill>
        </div>
        <div v-else class="font-semibold whitespace-nowrap text-gray-600">
          {{ $t("SCORECARDS.PTS", { points }) }}
        </div>
      </div>
      <div class="grid grid-cols-3 gap-10">
        <scorecard-tile-small
          v-for="scorecard in scorecardGroup?.score_cards || []"
          :key="scorecard.id"
          :scorecard="scorecard"
          :group="scorecardGroup"
        />
      </div>
    </div>
  </div>
  <scorecard-preview-modal
    v-if="!pageIsView && showPreviewModal"
    :model="scorecardGroup"
    :close="closePreviewModal"
  />
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";

import { Ability, PermissionSubject } from "@/enums/auth";
import {
  SCORECARD_STATUS_DRAFT,
  SCORECARD_TYPE_MANUAL,
} from "@/helpers/constants";
import { getColorId } from "@/helpers/scorecards";
import {
  ScorecardsIsViewKey,
  ScorecardsIsWorkflowBuilder,
} from "@/helpers/symbols";
import { usePermissions } from "@/hooks/auth";
import { useDeals } from "@/hooks/deals";
import { useScorecards } from "@/hooks/scorecards";
import { useActiveStage } from "@/hooks/workflow";

import RunScorecardButton from "./RunScorecardButton.vue";
import ScorecardActionMenu from "./ScorecardActionMenu.vue";
import ScorecardFavoriteButton from "./ScorecardFavoriteButton.vue";
import ScorecardIdBadge from "./ScorecardIdBadge.vue";
import ScorecardPreviewModal from "./ScorecardPreviewModal.vue";
import ScorecardRiskTierPill from "./ScorecardRiskTierPill.vue";
import ScorecardTileSmall from "./ScorecardTileSmall.vue";

import type { IScorecardGroup } from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits<{
  (eventName: "tileSelect", id: string): void;
}>();

const props = defineProps({
  scorecardGroup: {
    type: Object as PropType<IScorecardGroup>,
    required: true,
  },
  select: {
    type: Boolean,
    default: false,
  },
  selectedIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
});

const { canPerformActionReactive } = usePermissions();
const { activeStage } = useActiveStage();
const { activeDeal } = useDeals();

const showPreviewModal = ref(false);
const tileIsHovered = ref(false);
const pageIsView = inject(ScorecardsIsViewKey, true);
const isWorkflowBuilder = inject(ScorecardsIsWorkflowBuilder);

const { canUseScorecardActions } = useScorecards(false);

const canRunScoreCardGroup = canPerformActionReactive(
  PermissionSubject.scoreCardGroup,
  Ability.run,
  {
    scoreCardGroup: props.scorecardGroup.id,
    scoreCardStageId: activeStage.value?.id,
    application: activeDeal.value.id,
  }
);

const canUpdateScoreCardGroup = canPerformActionReactive(
  PermissionSubject.scoreCardGroup,
  Ability.update,
  {
    scoreCardGroup: props.scorecardGroup.id,
  }
);

const isWorkflowLive = !pageIsView && !isWorkflowBuilder;

const points = computed(() => {
  if (pageIsView) {
    return props.scorecardGroup.points || "-";
  }
  const points = props.scorecardGroup.results?.[0]?.points || 0;
  const total = props.scorecardGroup.points || 0;
  return `${points}/${total}`;
});

const results = computed(() => {
  return props.scorecardGroup?.results || [];
});
const riskTier = computed(() => {
  return results.value[0]?.score_card_group_risk_tier;
});
const groupStatus = computed(() => {
  return results.value[0]?.score_card_group_status;
});

const handleCheckboxInput = () => {
  if (!props.select) {
    return;
  }
  emit("tileSelect", props.scorecardGroup.id);
};

const closePreviewModal = () => {
  showPreviewModal.value = false;
};

const openPreviewModal = () => {
  showPreviewModal.value = true;
};
</script>
